// src/components/dashboard/StaffDetailReport.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
} from "@mui/material";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const StaffDetailReport = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      setIsDark(
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme")
      );
    };
    checkTheme();
    const obs = new MutationObserver(checkTheme);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  // Filter States
  const [staff, setStaff] = useState("");
  const [staffStatus, setStaffStatus] = useState("All");

  // Pagination & Dataset States (Matching Screenshot Data)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const [staffRows] = useState([
    { id: 86, name: "TruEvv", email: "awais@gmail.com", staffId: 86, gender: "Male", active: "Yes" },
    { id: 246, name: "Ali Ammar", email: "ammarjaffri605@gmail.com", staffId: 246, gender: "", active: "Yes" },
    { id: 252, name: "usman", email: "rajausmanshanee@gmail.com", staffId: 252, gender: "", active: "Yes" },
    { id: 254, name: "dssd", email: "ru0542668@gmail.com", staffId: 254, gender: "", active: "No" },
    { id: 255, name: "ASAD ARSHAD", email: "asadoffice63325@gmail.com", staffId: 255, gender: "", active: "Yes" },
    { id: 256, name: "Asadd", email: "asadx13846@gmail.com", staffId: 256, gender: "", active: "Yes" },
    { id: 257, name: "dsaf", email: "dgfg@gmail.com", staffId: 257, gender: "", active: "Yes" },
  ]);

  const tealMain = "#1E5E5F";
  const tealButton = "#245E65";
  const labelTeal = "#276A6C";
  const pastelTealBg = "#D9EBEF";
  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#245E65";
  const softCardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#EAEFF4";
  const primaryText = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryText = isDark ? "#94A3B8" : "#64748B";
  const controlBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const controlBorder = isDark ? "rgba(255, 255, 255, 0.12)" : "#CBD5E1";
  const dropdownBg = isDark ? "#1E293B" : "#FFFFFF";

  const handleClear = () => {
    setStaff("");
    setStaffStatus("All");
  };

  const filteredRows = staffRows.filter((r) => {
    const matchStaff = staff === "" || r.name.toLowerCase().includes(staff.toLowerCase());
    const matchStatus = staffStatus === "All" || (staffStatus === "Active" ? r.active === "Yes" : r.active === "No");
    return matchStaff && matchStatus;
  });

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: dropdownBg,
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#B8D6DD"}`,
        borderRadius: "8px",
        color: primaryText,
        boxShadow: isDark ? "0 12px 32px rgba(0, 0, 0, 0.6)" : "0 8px 24px rgba(15, 23, 42, 0.08)",
        "& .MuiMenuItem-root": {
          fontSize: "0.82rem",
          py: 0.8,
          "&:hover": { backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : pastelTealBg },
          "&.Mui-selected": { backgroundColor: isDark ? "rgba(30, 94, 95, 0.35)" : pastelTealBg, color: tealButton, fontWeight: 700 },
        },
      },
    },
  };

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* 1. Header Banner */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.2, md: "20px 26px" },
          display: "flex",
          alignItems: "center",
          gap: 2,
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "14px",
            backgroundColor: tealMain,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <BadgeRoundedIcon sx={{ fontSize: "1.75rem" }} />
        </Box>

        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Typography sx={{ fontSize: { xs: "1.2rem", md: "1.4rem" }, fontWeight: 800, color: primaryText }}>
              Staff Detail Report
            </Typography>
            <Chip
              label="Personnel"
              size="small"
              sx={{
                height: "22px",
                fontSize: "0.68rem",
                fontWeight: 700,
                backgroundColor: pastelTealBg,
                color: tealButton,
                borderRadius: "6px",
              }}
            />
          </Box>
          <Typography sx={{ fontSize: "0.82rem", color: secondaryText, fontWeight: 500, mt: 0.3 }}>
            View detailed staff information and records
          </Typography>
        </Box>
      </Box>

      {/* 2. Filter Section */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.5, md: "26px 30px" },
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <SearchIcon sx={{ color: labelTeal, fontSize: "1.3rem" }} />
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: primaryText }}>
            Filter Staff Details
          </Typography>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>
          {/* Staff Select */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Staff</Typography>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={staff}
              onChange={(e) => setStaff(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(selected) => (selected ? selected : <Typography sx={{ color: secondaryText, fontSize: "0.85rem" }}>Select staff...</Typography>)}
              sx={{ borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } }}
            >
              <MenuItem value="">All Staff</MenuItem>
              <MenuItem value="TruEvv">TruEvv</MenuItem>
              <MenuItem value="Ali Ammar">Ali Ammar</MenuItem>
              <MenuItem value="usman">usman</MenuItem>
              <MenuItem value="ASAD ARSHAD">ASAD ARSHAD</MenuItem>
            </Select>
          </Box>

          {/* Staff Status */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Staff Status</Typography>
            <Box sx={{ position: "relative" }}>
              <Select
                fullWidth
                size="small"
                value={staffStatus}
                onChange={(e) => setStaffStatus(e.target.value)}
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={menuProps}
                sx={{ borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } }}
                endAdornment={
                  staffStatus !== "All" && (
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setStaffStatus("All"); }} sx={{ mr: 2, color: secondaryText, p: 0.3 }}>
                      <CloseRoundedIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  )
                }
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </Box>
          </Box>
        </Box>

        {/* Search & Clear Buttons */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3.5 }}>
          <Button
            variant="contained"
            sx={{ height: "40px", backgroundColor: tealMain, color: "#FFFFFF", fontWeight: 700, fontSize: "0.84rem", borderRadius: "8px", px: 3, textTransform: "none", boxShadow: "none", "&:hover": { backgroundColor: tealButton } }}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            onClick={handleClear}
            startIcon={<DeleteOutlineRoundedIcon />}
            sx={{ height: "40px", borderColor: tealButton, color: tealButton, fontWeight: 700, fontSize: "0.84rem", borderRadius: "8px", px: 2.8, textTransform: "none" }}
          >
            Clear
          </Button>
        </Box>
      </Box>

      {/* 3. Results Table Container */}
      <Box sx={{ backgroundColor: cardBg, borderRadius: "16px", border: `1px solid ${softCardBorder}`, overflow: "hidden" }}>
        <TableContainer sx={{ minHeight: "350px" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ "& th": { color: primaryText, fontWeight: 800, fontSize: "0.86rem", backgroundColor: isDark ? "#162238" : "#F8FAFC", py: 2, px: 3 } }}>
                <TableCell>Staff Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Staff Id</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>Active</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 10, textAlign: "center" }}>
                    <Typography sx={{ color: secondaryText, fontSize: "0.9rem", fontWeight: 600 }}>No rows</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                  <TableRow key={row.id} sx={{ "&:hover": { backgroundColor: isDark ? "rgba(30, 94, 95, 0.08)" : "rgba(36, 94, 101, 0.03)" } }}>
                    <TableCell sx={{ fontWeight: 600, color: tealButton, cursor: "pointer", textDecoration: "underline" }}>{row.name}</TableCell>
                    <TableCell sx={{ color: primaryText }}>{row.email}</TableCell>
                    <TableCell sx={{ color: primaryText, fontWeight: 600 }}>{row.staffId}</TableCell>
                    <TableCell sx={{ color: secondaryText }}>{row.gender || ""}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 700, color: row.active === "Yes" ? "#15803D" : "#DC2626", fontSize: "0.88rem" }}>
                        {row.active}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{ borderTop: `1px solid ${softCardBorder}`, color: secondaryText }}
        />
      </Box>
    </Box>
  );
};

export default StaffDetailReport;